import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus, Skull, RefreshCw, Loader } from 'lucide-react';
import ModuleContainer from '../layout/ModuleContainer.jsx';
import InfoButton from '../common/InfoButton.jsx';

const PROFILE_CONFIG = {
  CONTINUATION:      { icon: TrendingUp,  color: 'text-green-400',      bg: 'bg-green-500/10',       barColor: '#22c55e', label: 'Continuation' },
  REVERSAL:          { icon: RefreshCw,    color: 'text-blue-400',       bg: 'bg-blue-500/10',        barColor: '#3b82f6', label: 'Reversal' },
  CONSOLIDATION:     { icon: Minus,        color: 'text-amber-400',      bg: 'bg-amber-500/10',       barColor: '#f59e0b', label: 'Consolidation' },
  SEEK_AND_DESTROY:  { icon: Skull,        color: 'text-red-400',        bg: 'bg-red-500/10',         barColor: '#ef4444', label: 'Seek & Destroy' },
};

export default function SessionProfile({ instrument }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchSession() {
      try {
        const res = await fetch(`/api/futures-signals/session?instrument=${instrument}`);
        if (res.ok) setData(await res.json());
      } catch { /* silent */ }
      finally { setLoading(false); }
    }
    fetchSession();
    const interval = setInterval(fetchSession, 30000);
    return () => clearInterval(interval);
  }, [instrument]);

  const session = data?.session;
  const profile = session?.ny_profile;
  const config = PROFILE_CONFIG[profile] || null;
  const Icon = config?.icon || Loader;

  return (
    <ModuleContainer title={<span>Session Profile <InfoButton title="NY Session Profiles (ICT)">
        <p><b>Continuation</b> — London moved 60%+ of range in one direction. NY continues that move. Trade WITH London's direction.</p>
        <p><b>Reversal</b> — London tagged a higher-timeframe level (previous day H/L, weekly H/L) then reversed. NY trades AGAINST London's direction.</p>
        <p><b>Consolidation</b> — London range was too small (under threshold) or price crossed the midpoint 3+ times. No clear direction — avoid trading.</p>
        <p><b>Seek & Destroy</b> — Both London High AND Low were swept (stop hunts on both sides). The most dangerous profile — stay flat.</p>
      </InfoButton></span>} subtitle="London levels & NY classification">
      {/* Asia + London Levels */}
      <div className="grid grid-cols-3 gap-2 mb-2">
        {[
          { label: 'Asia High', value: session?.asia_high, color: 'text-green-400' },
          { label: 'Asia Low', value: session?.asia_low, color: 'text-green-400' },
          { label: 'Asia Range', value: session?.asia_high && session?.asia_low ? session.asia_high - session.asia_low : null, color: 'text-korinth-muted' },
        ].map(({ label, value, color }) => (
          <div key={label} className="text-center bg-korinth-bg rounded px-2 py-1.5 border border-green-500/10">
            <div className={`text-xs font-mono font-bold ${color}`}>{value != null ? value.toFixed(2) : '—'}</div>
            <div className="text-[9px] text-korinth-muted">{label}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-4 gap-2 mb-4">
        {[
          { label: 'London High', value: session?.london_high, color: 'text-blue-400' },
          { label: 'London Low', value: session?.london_low, color: 'text-blue-400' },
          { label: 'Range', value: session?.london_range, color: 'text-korinth-text' },
          { label: 'Midpoint', value: session?.london_high && session?.london_low
            ? ((session.london_high + session.london_low) / 2) : null, color: 'text-korinth-muted' },
        ].map(({ label, value, color }) => (
          <div key={label} className="text-center bg-korinth-bg rounded px-2 py-1.5 border border-blue-500/10">
            <div className={`text-xs font-mono font-bold ${color}`}>{value != null ? value.toFixed(2) : '—'}</div>
            <div className="text-[9px] text-korinth-muted">{label}</div>
          </div>
        ))}
      </div>

      {/* Lock Status */}
      {!session?.locked && session && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-blue-500/10 rounded border border-blue-500/20">
          <Loader className="w-3 h-3 text-blue-400 animate-spin" />
          <span className="text-xs font-mono text-blue-400">TRACKING — London session in progress</span>
        </div>
      )}

      {/* NY Profile Classification */}
      {config && session?.locked && (
        <div className={`rounded-lg p-4 ${config.bg} border border-korinth-border/50`}>
          <div className="flex items-center gap-3">
            <Icon className={`w-8 h-8 ${config.color}`} />
            <div>
              <h3 className={`text-lg font-mono font-bold ${config.color}`}>
                {config.label}
                <InfoButton title={`${config.label} Profile`}>
                  {profile === 'CONTINUATION' && <p>London moved 60%+ of its range in one direction. NY is expected to <b>continue</b> that move. Trade WITH London's direction — this is the highest-probability profile.</p>}
                  {profile === 'REVERSAL' && <p>London tagged a higher-timeframe level (PDH/PDL or weekly H/L) and reversed. NY trades <b>against</b> London's direction. Look for a clean retrace into OTE for entry.</p>}
                  {profile === 'CONSOLIDATION' && <p>London range was too small (choppy, below threshold) or price crossed the midpoint 3+ times. <b>No clear direction — avoid trading.</b> Wait for the next session.</p>}
                  {profile === 'SEEK_AND_DESTROY' && <p>Both the London High AND Low were swept (stop hunts on both sides). This is the most <b>dangerous</b> profile. The market is hunting stops in both directions. <b>Stay flat.</b></p>}
                </InfoButton>
              </h3>
              <p className="text-xs text-korinth-muted">
                {session.profile_confidence ? `${session.profile_confidence.toFixed(0)}% confidence` : ''}
              </p>
            </div>
          </div>

          {/* Confidence bar */}
          <div className="mt-3 h-1.5 bg-korinth-bg rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${session.profile_confidence || 0}%`,
                backgroundColor: config.barColor,
              }}
            />
          </div>

          {/* Reasoning */}
          {session.profile_reasoning && (
            <div className="mt-3 text-[10px] font-mono text-korinth-muted space-y-0.5">
              {Object.entries(session.profile_reasoning).map(([k, v]) => (
                <div key={k}><span className="text-korinth-text">{k}:</span> {String(v)}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {!session && !loading && (
        <p className="text-xs text-korinth-muted font-mono py-4 text-center">
          No session data yet. Tracking begins during London session.
        </p>
      )}
    </ModuleContainer>
  );
}
